<template>
	<div class="flex">
		<DropdownStatus
			v-model="temporaryStatus"
			checkmark
			:options="props.statusOptions"
			id="publishingStatus"
			optionLabel="title"
			optionValue="identifier"
			@change="switchStatus"
			placeholder="Select Status"
			class="w-full border"
			:class="{
				'border-green-500 focus:border-green-500 active:border-green-500 outline-green-500': selectedPublishingStatus?.identifier === 'p',
				'border-danger-700 focus:border-danger-700 active:border-danger-700 outline-danger-700': selectedPublishingStatus?.identifier === 'd' || selectedPublishingStatus?.identifier === 'n',
				'border-gray-400 focus:border-gray-400 active:border-gray-400 outline-gray-400': selectedPublishingStatus?.identifier === 'a' || selectedPublishingStatus?.identifier === 'e',
				'border-warning-700 focus:border-warning-700 active:border-warning-700 outline-warning-700': selectedPublishingStatus?.identifier !== 'p' && selectedPublishingStatus?.identifier !== 'd' && selectedPublishingStatus?.identifier !== 'e'  && selectedPublishingStatus?.identifier !== 'a' && selectedPublishingStatus?.identifier !== 'n'
			}"
		>
			<template #value>
				<div class="flex items-center text-sm"
					:class="{
						'text-green-700': selectedPublishingStatus?.identifier === 'p',
						'text-danger-700': selectedPublishingStatus?.identifier === 'd' || selectedPublishingStatus?.identifier === 'n',
						'text-gray-400': selectedPublishingStatus?.identifier === 'e',
						'text-warning-700': selectedPublishingStatus?.identifier !== 'p' && selectedPublishingStatus?.identifier !== 'd'  && selectedPublishingStatus?.identifier !== 'e'  && selectedPublishingStatus?.identifier !== 'a' && selectedPublishingStatus?.identifier !== 'n'
					}">
					<i v-if="selectedPublishingStatus?.identifier === 'a'" class="tabler:archive w-5 h-5 mr-1 color-danger-700"></i>
					<i v-if="selectedPublishingStatus?.identifier === 'd' || selectedPublishingStatus?.identifier === 'n'" class="tabler:info-circle w-5 h-5 mr-1 color-danger-700"></i>
					<i v-if="selectedPublishingStatus?.identifier === 'e'" class="tabler:files-off w-5 h-5 mr-1 color-danger-400"></i>
					<i v-if="selectedPublishingStatus?.identifier === 'p'" class="tabler:circle-check w-5 h-5 mr-1 color-green"></i>
					<i v-if="selectedPublishingStatus?.identifier === 'r'" class="tabler:file-check w-5 h-5 mr-1 color-warning"></i>
					<i v-if="selectedPublishingStatus?.identifier !== 'p' && selectedPublishingStatus?.identifier !== 'e' && selectedPublishingStatus?.identifier !== 'd' && selectedPublishingStatus?.identifier !== 'a' && selectedPublishingStatus?.identifier !== 'n' && selectedPublishingStatus?.identifier !== 'r'" class="tabler:info-circle w-5 h-5 mr-1 color-dark"></i>
					{{ selectedPublishingStatus?.title }}
				</div>
			</template>

			<template #option="slotProps">
				<div class="flex items-center text-sm">
					<div>{{ slotProps.option.title }}</div>
				</div>
			</template>
		</DropdownStatus>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { PublishingStatusList } from '@/types/cvTypes'

import Swal from 'sweetalert2'
import { useAppStore } from '@/stores/app-store'

const { currentLng } = useAppStore()

const props = defineProps<{
	jsonData: {
		identifier: string;
		publishingStatusCode: string;
		_links: { self: { href: string } };
	};
	statusOptions?: PublishingStatusList[];
}>()

const emit = defineEmits<{
	publishingStatusChange: [{ uid: string; newStatus: string }]
	statusChange: [string]

}>()

const publishingCode = ref(props.jsonData.publishingStatusCode)
const temporaryStatus = ref(publishingCode.value)

watch(
	() => props.jsonData.publishingStatusCode,
	(newVal) => {
		publishingCode.value = newVal
		temporaryStatus.value = newVal
	}
)

const selectedPublishingStatus = computed(() => {
	return props.statusOptions?.find(item => item.identifier === publishingCode.value) || {
		identifier: 'u',
		title: 'Unpublished'
	}
})

async function switchStatus(event: { value: string }) {
	const selectedStatus = props.statusOptions?.find(item => item.identifier === event.value)
	const packageUID = props.jsonData.identifier
	temporaryStatus.value = event.value

	if (!selectedStatus) return

	const patchStatus = async () => {
		try {
			const selfHref = props.jsonData?._links?.self?.href
			if (!selfHref) {
				throw new Error('Missing _links.self.href for publishing status update')
			}

			const url = new URL(selfHref, window.location.origin)
			if (currentLng.value) {
				url.searchParams.set('lng', String(currentLng.value))
			}

			const response = await fetch(url.toString(), {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					publishingStatusCode: selectedStatus.identifier
				})
			})

			if (response.ok) {
				publishingCode.value = selectedStatus.identifier
				emit('publishingStatusChange', { uid: packageUID, newStatus: selectedStatus.identifier })
				emit('statusChange', selectedStatus.identifier)

				await Swal.fire({
					title: 'Status Changed!',
					html: `Publishing status updated to <span class="text-danger"><b>"<u>${selectedStatus.title}</u>"</b></span>.`,
					icon: 'success',
					confirmButtonColor: '#15803d'
				})
			} else {
				throw new Error(`Failed with status ${response.status}`)
			}
		} catch (error) {
			console.error('Error updating status:', error)
			await Swal.fire({
				title: 'Update Failed',
				text: 'An error occurred while updating the status.',
				icon: 'error',
				confirmButtonColor: '#d33'
			})
		}
	}

	if (selectedStatus.identifier) {
		const result = await Swal.fire({
			title: 'Are you sure?',
			text: 'Do you want to change the publishing status of this item?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#4679cc',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes'
		})

		if (result.isConfirmed) {
			await patchStatus()
		} else {
			temporaryStatus.value = publishingCode.value
		}
	} else {
		await patchStatus()
	}
}
</script>

<style>
#publishingStatus .p-inputtext {
	padding-left: 0.5rem !important;
	padding-right: 0 !important;
	padding-top: 0.5rem !important;
	padding-bottom: 0.5rem !important;
}
</style>
